/**
 * =====================================================================
 * Centralized API Error Handler
 * =====================================================================
 * Unified error handling for all API services to eliminate duplication.
 *
 * Features:
 * - Single ApiServiceError class for all services
 * - Consistent error message extraction
 * - Automatic status code and details capture
 * - Support for both AxiosError and generic errors
 * - Centralized logging integration
 *
 * Usage:
 * ```typescript
 * import { ApiServiceError, handleApiError } from '@/utils/api-error-handler';
 *
 * try {
 *   const response = await apiClient.get('/endpoint');
 *   return response.data;
 * } catch (error) {
 *   return handleApiError(error, 'fetch data', 'MyService');
 * }
 * ```
 *
 * CREATED: 2025-10-09 - Consolidates duplicate error handling from:
 * - clientApi.ts, invoiceApi.ts, vehicle.service.ts, ocr.service.ts
 * IMPACT: Reduces ~200 lines to ~50 lines (75% reduction)
 * =====================================================================
 */

import { AxiosError } from 'axios';
import { getErrorMessage } from '../services/api.service';
import { logger } from './logger';

// ===================================================================
// API SERVICE ERROR CLASS
// ===================================================================

/**
 * Standard error class for all API service operations
 */
export class ApiServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: any,
    public serviceName?: string
  ) {
    super(message);
    this.name = serviceName ? `${serviceName}Error` : 'ApiServiceError';
  }
}

// ===================================================================
// ERROR EXTRACTION UTILITIES
// ===================================================================

/**
 * Extract error details from AxiosError or generic error
 */
export function extractErrorDetails(error: any): {
  message: string;
  statusCode?: number;
  details?: any;
} {
  if (error instanceof AxiosError) {
    // AxiosError - extract from response
    const message =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.message ||
      'API request failed';

    const statusCode = error.response?.status;
    const details = error.response?.data?.details || error.response?.data;

    return { message, statusCode, details };
  }

  // Generic error or Error instance
  if (error instanceof Error) {
    return {
      message: error.message,
      statusCode: (error as any).status || (error as any).statusCode,
      details: (error as any).details || (error as any).detail,
    };
  }

  // Unknown error type
  return {
    message: getErrorMessage(error) || 'Unknown error occurred',
    statusCode: undefined,
    details: error,
  };
}

// ===================================================================
// ERROR HANDLER
// ===================================================================

/**
 * Centralized error handler for all API services
 *
 * @param error - The error object (AxiosError, Error, or any)
 * @param operation - Description of the operation that failed
 * @param serviceName - Optional service name for logging context
 * @throws {ApiServiceError} Always throws with extracted error details
 */
export function handleApiError(
  error: any,
  operation: string,
  serviceName?: string
): never {
  // Extract error details
  const { message, statusCode, details } = extractErrorDetails(error);

  // Log the error with context
  const logPrefix = serviceName ? `[${serviceName}]` : '[API]';
  logger.error(`${logPrefix} Error during ${operation}`, {
    error,
    statusCode,
    details,
  });

  // Throw standardized error
  throw new ApiServiceError(message, statusCode, details, serviceName);
}

// ===================================================================
// LEGACY COMPATIBILITY (Optional)
// ===================================================================

/**
 * Create a service-specific error handler with preset service name
 *
 * @param serviceName - The service name to use in logs and error class
 * @returns A handleApiError function with serviceName preset
 *
 * @example
 * const handleClientError = createServiceErrorHandler('ClientAPI');
 * // Later in code:
 * catch (error) {
 *   return handleClientError(error, 'fetch client');
 * }
 */
export function createServiceErrorHandler(serviceName: string) {
  return (error: any, operation: string): never => {
    return handleApiError(error, operation, serviceName);
  };
}
